import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
} from "@chakra-ui/react";
import {
  CUSTOM_PRIORITY_BAR_COLORS,
  CUSTOM_PRIORITY_COLOR_SCHEME,
  PRIORITY_BAR_COLORS,
  PRIORITY_COLOR_SCHEME,
} from "@components/modules/resource-planning/tasks/components/task-priority-button/constants";
import type {
  TaskPriorityBadgeProps,
  TaskPriorityIconProps,
} from "@components/modules/resource-planning/tasks/components/task-priority-button/types";
import { Icon } from "@components/ui-kit/Icon";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import type { FunctionComponent } from "react";

export const TaskPriorityButton: FunctionComponent<TaskPriorityBadgeProps> = ({
  asIcon,
  priority,
  taskPriorities,
  onChange,
  isLoading,
  disabled = false,
  variant = "ghost",
  rounded = "base",
  size = "xs",
  onClick,
  ...props
}) => (
  <WrapComponent
    if={!disabled && !!taskPriorities?.length}
    with={(children) => (
      <Menu isLazy>
        <MenuButton
          {...props}
          as={Button}
          colorScheme={
            priority.system_priority
              ? PRIORITY_COLOR_SCHEME[priority.system_priority]
              : CUSTOM_PRIORITY_COLOR_SCHEME
          }
          variant={variant}
          rounded={rounded}
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
        >
          {isLoading ? <Spinner color="teal.500" size="xs" /> : children}
        </MenuButton>
        <Portal>
          <MenuList>
            {taskPriorities?.map((priority) => (
              <MenuItem
                key={priority.name}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(priority.id);
                }}
              >
                <TaskPriorityIcon priority={priority} />
                <span>{priority.name}</span>
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      </Menu>
    )}
  >
    <TaskPriorityIcon priority={priority} asIcon={asIcon} />
    {!asIcon && priority.name}
  </WrapComponent>
);

/** The emoji if set, else the icon of the system priority level. */
export function TaskPriorityIcon({
  priority: { system_priority: systemPriority, emoji },
  asIcon = false,
}: TaskPriorityIconProps) {
  if (emoji) {
    return (
      <Box
        as="span"
        alignItems="center"
        justifyContent="center"
        display="inline-flex"
        flexShrink={0}
        boxSize="4"
        mr={asIcon ? "0" : "2"}
        fontSize="sm"
        lineHeight="1"
        aria-hidden
      >
        {emoji}
      </Box>
    );
  }

  return (
    <Icon
      boxSize="4"
      name={
        systemPriority === "URGENT"
          ? "alert-square"
          : systemPriority
            ? "priority-bars"
            : "priority-bars-stripe"
      }
      mr={asIcon ? "0" : "2"}
      sx={
        systemPriority
          ? PRIORITY_BAR_COLORS[systemPriority]
          : CUSTOM_PRIORITY_BAR_COLORS
      }
    />
  );
}
